import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useT, useLanguage } from "@/lib/LanguageContext";
import { Link } from "react-router-dom";
import { GraduationCap, Video, ExternalLink, AlertTriangle } from "lucide-react";
import IdCell from "./IdCell";
import { PageLoadError } from "./NoAccess";
import { DASH, DateText } from "./globalDataCells";
import { tenantDisplayName } from "./tenantDisplay";
import { pluralKey } from "../lib/pluralKey";

/**
 * Super-admin global view: every LMS course and every consultation door row, cross-tenant.
 *
 * THE ROOM URL IS NOT SHOWN, AND MUST NOT BE ADDED BACK. `video_rooms` is the consult
 * door table, and on Jitsi the URL *is* the room — whoever knows the string is in the
 * consultation. `GET /api/super-admin/global/rooms` therefore answers an explicit
 * mapping (`super_admin_learn_telehealth._room_row`) that never carries `room_url`,
 * `room_token`, `provider_room_name` or `provider_room_id`: the view answers who, when,
 * which provider and whether the door can open, never how to join. An operator who must
 * enter a consultation goes through the ordinary door
 * (`GET /api/consults/{appointmentId}/access`), which records the arrival.
 *
 * A room card shows: the practice (`tenant`, or the raw id when its document is gone), the
 * client, the consult session id (the join key to the money object), WHEN the consultation
 * is (`starts_at`, else the creation date) with the server-computed `timing` chip, the
 * provider, and `join_link` — `ready`, `missing` (the door re-provisions on entry, which the
 * card only says while the consultation is upcoming or in progress) or `none` (a
 * legacy-lane row that has no door). The stored `status` is not shown: no writer
 * ever advances it past "scheduled".
 *
 * This screen rendered `{r.room_url}` in a monospace line until 2026-09-02.
 */

const JOIN_BADGE = "inline-flex items-center gap-1 text-[10px] uppercase px-2 py-0.5 rounded";
const TIMING_TONES = {
  upcoming: "bg-sky-100 text-sky-700",
  in_progress: "bg-emerald-100 text-emerald-700",
  past: "bg-slate-100 text-slate-500",
};

/** The practice's 360 link. Keyed on `tenant_id` — the slug is not the route key. */
function ViewTenantLink({ tenant, tenantId, testId }) {
  const t = useT("superadmin");
  if (tenant) {
    return (
      <span data-testid={testId}>
        <Link to={`/super-admin/tenants/${tenant.tenant_id}`} className="text-xs text-amber-600 hover:underline inline-flex items-center gap-1">
          <ExternalLink className="w-3 h-3" /> {t("glt.viewTenant")}
        </Link>
      </span>
    );
  }
  if (tenantId) {
    return <span data-testid={testId} className="text-xs text-slate-400">{t("glt.tenantMissing", { id: tenantId })}</span>;
  }
  return null;
}

const practiceLabel = (tenant, tenantId) => (tenant ? tenantDisplayName(tenant) : tenantId) || DASH;

// Timings at which entering re-provisions a missing room. After the hard close the door
// refuses entry, and a row with no scheduled time says nothing about the door's window.
const REPROVISION_TIMINGS = new Set(["upcoming", "in_progress"]);

function JoinStatus({ value, timing, testId }) {
  const t = useT("superadmin");
  let body;
  if (value === "ready") {
    body = <span className={`${JOIN_BADGE} bg-emerald-100 text-emerald-600`}>{t("glt.join.ready")}</span>;
  } else if (value === "missing") {
    body = (
      <>
        <span className={`${JOIN_BADGE} bg-amber-100 text-amber-700`}>
          <AlertTriangle className="w-3 h-3" /> {t("glt.join.missing")}
        </span>
        {REPROVISION_TIMINGS.has(timing) && (
          <span className="block text-xs text-slate-400 mt-1">{t("glt.join.missingHint")}</span>
        )}
      </>
    );
  } else if (value === "none") {
    body = <span className={`${JOIN_BADGE} bg-slate-100 text-slate-500`}>{t("glt.lane.legacy")}</span>;
  } else {
    body = <span className={`${JOIN_BADGE} bg-slate-100 text-slate-500`}>{String(value ?? DASH)}</span>;
  }
  return <div data-testid={testId}>{body}</div>;
}

export default function GlobalLearnTelehealth() {
  const t = useT("superadmin");
  const tc = useT("common");
  const { lang } = useLanguage();
  const [tab, setTab] = useState("courses");
  // The courses read: the newest page (`courses`), how many exist (`total`), and EVERY
  // published course with no lesson whatever its age (`published_without_lessons`) — those
  // are the oldest rows, the ones the page cuts off, so the report cannot be a filter of it.
  const [courseList, setCourseList] = useState({ courses: [], total: 0, published_without_lessons: [] });
  const [rooms, setRooms] = useState([]);
  // Per list: a failed read is shown as a failure, never as "no courses" / "no rooms".
  const [coursesError, setCoursesError] = useState(null);
  const [roomsError, setRoomsError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setCoursesError(null);
    setRoomsError(null);
    api.get("/super-admin/global/courses").then(({ data }) => setCourseList(data)).catch(setCoursesError);
    api.get("/super-admin/global/rooms").then(({ data }) => setRooms(data)).catch(setRoomsError);
  }, [attempt]);
  const retry = () => setAttempt((n) => n + 1);
  const { courses, total: courseTotal, published_without_lessons: emptyPublished } = courseList;

  return (
    <div className="space-y-6" data-testid="sa-global-lms-telehealth">
      <div>
        <h1 className="font-sans text-3xl text-slate-800">{t("glt.title")}</h1>
        <p className="text-slate-500 text-sm mt-1">{t("glt.subtitle")}</p>
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        {[
          { id: "courses", label: t("glt.tabCourses"), icon: GraduationCap, count: courseTotal },
          { id: "rooms", label: t("glt.tabRooms"), icon: Video, count: rooms.length },
        ].map((tab_) => {
          const Icon = tab_.icon;
          return (
            <button
              key={tab_.id}
              onClick={() => setTab(tab_.id)}
              data-testid={`sa-tab-${tab_.id}`}
              className={`px-4 py-2 text-sm border-b-2 flex items-center gap-2 ${
                tab === tab_.id ? "border-sol-gold text-amber-600" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              <Icon className="w-4 h-4" /> {tab_.label} <span className="text-xs opacity-60">({tab_.count})</span>
            </button>
          );
        })}
      </div>

      {tab === "courses" && (
        <div className="grid lg:grid-cols-3 gap-4">
          {emptyPublished.length > 0 && (
            <div className="col-span-full text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-testid="sa-empty-published-banner">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span data-testid="sa-empty-published-count">{t("glt.emptyPublished.banner", { n: emptyPublished.length })}</span>
              </div>
              <ul className="mt-2 ml-6 space-y-1">
                {emptyPublished.map((c) => (
                  <li key={c.id} className="flex flex-wrap items-center gap-x-2 [overflow-wrap:anywhere]" data-testid={`sa-empty-published-item-${c.id}`}>
                    <span className="text-slate-800">{c.title}</span>
                    <span className="text-xs text-amber-700">{practiceLabel(c.tenant, c.tenant_id)}</span>
                    <ViewTenantLink tenant={c.tenant} tenantId={c.tenant_id} testId={`sa-empty-published-tenant-${c.id}`} />
                  </li>
                ))}
              </ul>
            </div>
          )}
          {courseTotal > courses.length && (
            <p className="col-span-full text-xs text-slate-500" data-testid="sa-courses-truncated">
              {t("glt.coursesTruncated", { shown: courses.length, total: courseTotal })}
            </p>
          )}
          {coursesError && <div className="col-span-full"><PageLoadError error={coursesError} onRetry={retry} testId="sa-courses-load-error" /></div>}
          {!coursesError && courses.length === 0 && <p className="text-slate-400 col-span-full py-12 text-center">{t("glt.noCourses")}</p>}
          {courses.map((c) => (
            <div key={c.id} className="bg-white shadow-sm rounded-2xl border border-slate-200 p-4" data-testid={`sa-course-${c.id}`}>
              <div className="text-xs text-amber-600 uppercase tracking-wider mb-2">{practiceLabel(c.tenant, c.tenant_id)}</div>
              <h3 className="font-sol text-lg text-slate-800">{c.title}</h3>
              <p className="text-xs text-slate-500 mt-1 line-clamp-2">{c.description}</p>
              <div className="flex items-center justify-between mt-3 text-xs text-slate-400">
                <span data-testid={`sa-course-lessons-${c.id}`}>{tc(pluralKey(lang, c.lessons_count ?? 0, "count.lessons"), { n: c.lessons_count ?? 0 })}</span>
                <span>{c.price_cents > 0 ? `${(c.price_cents/100).toFixed(2)} ${c.currency?.toUpperCase()}` : t("glt.free")}</span>
                <span className={c.published ? "text-emerald-400" : "text-slate-400"}>{c.published ? t("glt.published") : t("glt.draft")}</span>
              </div>
              {c.publish_problem === "COURSE_HAS_NO_LESSONS" && (
                <div className={`${JOIN_BADGE} bg-amber-100 text-amber-700 mt-2`} data-testid={`sa-course-empty-badge-${c.id}`}>
                  <AlertTriangle className="w-3 h-3" /> {t("glt.emptyPublished.badge")}
                </div>
              )}
              <div className="mt-2"><ViewTenantLink tenant={c.tenant} tenantId={c.tenant_id} testId={`sa-course-tenant-${c.id}`} /></div>
            </div>
          ))}
        </div>
      )}

      {tab === "rooms" && (
        <div className="space-y-2">
          <PageLoadError error={roomsError} onRetry={retry} testId="sa-rooms-load-error" />
          {!roomsError && rooms.length === 0 && <p className="text-slate-400 py-12 text-center">{t("glt.noRooms")}</p>}
          {rooms.map((r) => (
            <div key={r.id} className="bg-white shadow-sm rounded-2xl border border-slate-200 p-4 flex flex-wrap items-start gap-x-6 gap-y-3" data-testid={`sa-room-${r.id}`}>
              <Video className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-[16rem]">
                <div className="flex flex-wrap items-center gap-x-2 mb-0.5">
                  <span className="text-xs text-amber-600">{practiceLabel(r.tenant, r.tenant_id)}</span>
                  <ViewTenantLink tenant={r.tenant} tenantId={r.tenant_id} testId={`sa-room-tenant-${r.id}`} />
                </div>
                <div className="text-slate-800 text-sm [overflow-wrap:anywhere]">{r.client_name || r.client_email}</div>
                <div className="flex items-center gap-1.5 text-xs text-slate-400" data-testid={`sa-room-session-${r.id}`}>
                  <span>{t("glt.sessionId")}</span>
                  <IdCell value={r.consultation_session_id} />
                </div>
              </div>
              <div className="flex-1 min-w-[14rem] text-xs text-slate-500 space-y-1" data-testid={`sa-room-when-${r.id}`}>
                {r.starts_at ? (
                  <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                    <span>{t("glt.room.scheduledFor")}</span>
                    <DateText value={r.starts_at} lang={lang} withTime />
                    {r.timing !== "unknown" && (
                      <span data-testid={`sa-room-timing-${r.id}`} className={`text-[10px] uppercase px-2 py-0.5 rounded ${TIMING_TONES[r.timing] || "bg-slate-100 text-slate-500"}`}>
                        {t(`glt.timing.${r.timing}`, undefined, String(r.timing))}
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-x-1.5">
                    <span>{t("glt.room.noSchedule")}</span>
                    <span aria-hidden="true">·</span>
                    <span>{t("glt.room.createdOn")}</span>
                    <DateText value={r.created_at} lang={lang} withTime />
                  </div>
                )}
                <div className="text-slate-500">{r.provider}</div>
              </div>
              <div className="min-w-[10rem]">
                <JoinStatus value={r.join_link} timing={r.timing} testId={`sa-room-join-${r.id}`} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
